import { useEffect, useState } from 'react';

/**
 * GlobalVyapar Custom Loading Screen
 * Features:
 * - Static logo (exact brand logo)
 * - Animated blue loading line underneath
 * - Clean, minimal design
 */
export default function LoadingScreen({ isLoading = true, minDuration = 600 }) {
  const [show, setShow] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setFadeOut(true);
        setTimeout(() => setShow(false), 250);
      }, minDuration);
      return () => clearTimeout(timer);
    } else {
      setShow(true);
      setFadeOut(false);
    }
  }, [isLoading, minDuration]);

  if (!show) return null;

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-white transition-opacity duration-250 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}
      data-testid="loading-screen"
    >
      <div className="flex flex-col items-center">
        {/* Exact Logo - Static */}
        <img 
          src="/logo-cropped.png" 
          alt="GlobalVyapar" 
          className="h-12 sm:h-14 w-auto"
        />
        
        {/* Blue Loading Line */}
        <div className="mt-4 h-0.5 w-32 sm:w-40 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full animate-loading-line"
            style={{ backgroundColor: '#2563eb', width: '40%' }}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Mini loader for inline/component loading states
 */
export function MiniLoader({ size = 'md', className = '' }) {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8'
  };

  return (
    <div className={`relative ${sizes[size]} ${className}`}>
      <div className="absolute inset-0 rounded-full border-2 border-primary/20" />
      <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-primary animate-spin" />
    </div>
  );
}

/**
 * Page transition loader
 */
export function PageLoader() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          <img 
            src="/logo-cropped.png" 
            alt="Loading" 
            className="h-12 w-auto animate-pulse"
          />
          <div className="absolute -inset-4 rounded-full border border-primary/20 animate-ping" />
        </div>
        <div className="flex gap-1">
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}
